import { canvasDocumentV3Schema, upgradeCanvasDocument, type CanvasDocumentV3, type CanvasDocument } from "@seedance/contracts";

type LegacyDraftDocument = Omit<CanvasDocument, "schemaVersion"> & { schemaVersion: 1 | 2 };

const databaseName = "seedance-studio-drafts";
const storeName = "drafts";

export interface CanvasDraft {
  /** Absent on legacy owner/project records. Each controller owns a fresh writer. */
  draftId?: string;
  writerId?: string;
  ownerId: string;
  projectId: string;
  baseRevision: string;
  document: CanvasDocumentV3 | LegacyDraftDocument;
  savedAt: string;
}

export interface CanvasDraftListing { drafts: CanvasDraft[]; invalidCount: number }

export interface CanvasDraftStore {
  get(ownerId: string, projectId: string): Promise<CanvasDraft | undefined>;
  list(ownerId: string, projectId: string): Promise<CanvasDraft[]>;
  listWithDiagnostics(ownerId: string, projectId: string): Promise<CanvasDraftListing>;
  put(draft: CanvasDraft): Promise<void>;
  remove(expected: CanvasDraft): Promise<void>;
}

interface StoredCanvasDraft extends CanvasDraft {
  key: string;
}

export interface CanvasDraftStoreOptions {
  indexedDB?: IDBFactory;
}

function validateDraftMetadata(value: Record<string, unknown>): void {
  const nonemptyString = (item: unknown): item is string => typeof item === "string" && item.length > 0;
  if (!nonemptyString(value.ownerId) || !nonemptyString(value.projectId)
    || typeof value.baseRevision !== "string" || !/^\d+$/u.test(value.baseRevision)
    || typeof value.savedAt !== "string" || !Number.isFinite(Date.parse(value.savedAt))
    || (value.draftId !== undefined && !nonemptyString(value.draftId))
    || (value.writerId !== undefined && !nonemptyString(value.writerId))
    || (value.draftId === undefined) !== (value.writerId === undefined)) {
    throw new Error("Invalid draft metadata");
  }
}

function draftKey(draft: CanvasDraft): string {
  return draft.draftId
    ? JSON.stringify([draft.ownerId, draft.projectId, draft.writerId, draft.draftId])
    : `${draft.ownerId}:${draft.projectId}`;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export function createCanvasDraftStore(
  options: CanvasDraftStoreOptions = {}
): CanvasDraftStore {
  const indexedDB = options.indexedDB ?? globalThis.indexedDB;

  async function open(): Promise<IDBDatabase> {
    const request = indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(storeName)) {
        request.result.createObjectStore(storeName, { keyPath: "key" });
      }
    };
    return requestResult(request);
  }

  return {
    async get(ownerId, projectId) {
      return (await this.list(ownerId, projectId))[0];
    },
    async list(ownerId, projectId) {
      return (await this.listWithDiagnostics(ownerId, projectId)).drafts;
    },
    async listWithDiagnostics(ownerId, projectId) {
      const database = await open();
      try {
        const transaction = database.transaction(storeName, "readonly");
        const complete = transactionComplete(transaction);
        const records = await requestResult(
          transaction.objectStore(storeName).getAll()
        );
        await complete;
        const drafts:CanvasDraft[] = [];
        let invalidCount = 0;
        for (const record of records as unknown[]) {
          // Never inspect or report another owner's/project's stored data.
          if (!record || typeof record !== "object" || Array.isArray(record)) continue;
          const scoped = record as Record<string,unknown>;
          if (scoped.ownerId !== ownerId || scoped.projectId !== projectId) continue;
          try {
            validateDraftMetadata(scoped);
            const {key,...rawDraft} = scoped;
            const draft = rawDraft as unknown as CanvasDraft;
            if (key !== draftKey(draft)) throw new Error("Invalid draft key");
            // Recognize legacy storage without migrating or dropping its raw source.
            // Structural V3 conversion is performed only after a recovery choice.
            if (draft.document?.schemaVersion === 3) canvasDocumentV3Schema.parse(draft.document);
            else upgradeCanvasDocument(draft.document);
            drafts.push(draft);
          } catch { invalidCount += 1; }
        }
        drafts.sort((a,b) => a.savedAt.localeCompare(b.savedAt));
        return {drafts,invalidCount};
      } finally {
        database.close();
      }
    },
    async put(draft) {
      const database = await open();
      try {
        const transaction = database.transaction(storeName, "readwrite");
        transaction.objectStore(storeName).put({
          ...draft,
          key: draftKey(draft)
        } satisfies StoredCanvasDraft);
        await transactionComplete(transaction);
      } finally {
        database.close();
      }
    },
    async remove(expected) {
      const database = await open();
      try {
        const transaction = database.transaction(storeName, "readwrite");
        const store = transaction.objectStore(storeName);
        const request = store.get(draftKey(expected));
        request.onsuccess = () => {
          const record = request.result as StoredCanvasDraft | undefined;
          if (!record) return;
          const { key, ...actual } = record;
          if (JSON.stringify(actual) === JSON.stringify(expected)) store.delete(key);
        };
        await transactionComplete(transaction);
      } finally {
        database.close();
      }
    }
  };
}
